"use client";


import { PALETTE } from "@/app/lib/colorPallete";
import axios, { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";


export const ForgotPasswordForm = ({ onSwitchToLogin }: { onSwitchToLogin: () => void }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post('/api/user/forgot-password', { email });
            setMessage(response.data.message);
            setValidationErrors({});
            setError(null);
        } catch (err) {
            const axiosError = err as AxiosError<{ message: string, errors?: Record<string, string[]> }>;
            setError(axiosError.response?.data?.message || 'An error occurred. Please try again.');
            if (axiosError.response?.data?.errors) {
                setValidationErrors(axiosError.response.data.errors);
            } else {
                setValidationErrors({});
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div key="forgot" initial={{ x: '100%', opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: '-100%', opacity: 0 }} transition={{ type: 'tween', ease: 'easeInOut' }}>
            <h2 className="text-2xl font-bold text-center font-serif" style={{ color: PALETTE.darkGreen }}>Reset Password</h2>
            <p className="text-center text-stone-500 mb-6 mt-2">Enter your email and we&apos;ll send you a link to reset your password.</p>

            {Object.keys(validationErrors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <h3 className="text-red-800 font-semibold mb-2 text-sm flex items-center gap-2">
                        Reset Errors
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-xs text-red-600">
                        {Object.entries(validationErrors).map(([field, messages]) => (
                            <li key={field}>
                                <span className="font-semibold capitalize">{field}</span>: {messages.join(', ')}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {error && !Object.keys(validationErrors).length && (
                <p className="text-center text-sm text-red-500 p-4 bg-red-50 rounded-md mb-6">{error}</p>
            )}
            {message ? (
                <p className="text-center text-sm text-green-600 font-semibold p-4 bg-green-50 rounded-md">{message}</p>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full p-3 rounded-md bg-stone-100 border border-stone-300 focus:ring-2 focus:ring-amber-500/80 text-stone-900 placeholder:text-stone-400" />
                    <button type="submit" disabled={isLoading} className="w-full py-3 mt-2 rounded-full font-semibold text-white flex items-center justify-center gap-2 disabled:bg-stone-400" style={{ backgroundColor: PALETTE.darkerGreen }}>
                        {isLoading ? <><Loader2 className="animate-spin" size={20} /> Sending Link...</> : 'Send Reset Link'}
                    </button>
                </form>
            )}
            <p className="text-center text-sm text-stone-500 mt-6">Remembered your password?{' '} <button onClick={onSwitchToLogin} className="font-semibold text-amber-600 hover:underline">Sign In</button></p>
        </motion.div>
    );
};